import { writable, type Writable } from 'svelte/store';

import { ArtAssetType } from '@do/models';

import { defaultsData, getOptions, getValues, getUpdatedOptions, getValueFromSlug, defaultValues } from '../configs';
import type { ArtworksFiltersModel, ArtworksFiltersOptionsResponse, FormValues } from '../types';

export const artworksFiltersCount = writable(0);

const createModel = (modelData: ArtworksFiltersModel) => {
  const { set, update, subscribe }: Writable<ArtworksFiltersModel> = writable(modelData);

  subscribe((current) => {
    const isDigitalOriginal = current.values.art_asset_type.includes(ArtAssetType.DigitalOriginal);

    if (current.initialized) {
      let count = 0;
      count += current.values.artists_slugs.length;
      count += current.values.curators_slugs.length;
      count += current.values.styles_slugs.length;
      count += current.values.medium_slugs.length;
      count += current.values.countries_slugs.length;

      if (current.values.art_asset_type && current.initialValues.art_asset_type) {
        if (current.values.art_asset_type !== current.initialValues.art_asset_type) count++;
      }

      if (current.values.price && current.initialValues.price) {
        if (current.values.price[0] !== current.initialValues.price[0]) count++;
        if (current.values.price[1] !== current.initialValues.price[1]) count++;
      }

      if (current.values.creation_year && current.initialValues.creation_year) {
        if (current.values.creation_year[0] !== current.initialValues.creation_year[0]) count++;
        if (current.values.creation_year[1] !== current.initialValues.creation_year[1]) count++;
      }

      if (current.values.original_size_x && current.initialValues.original_size_x) {
        if (current.values.original_size_x[0] !== current.initialValues.original_size_x[0] && isDigitalOriginal)
          count++;
        if (current.values.original_size_x[1] !== current.initialValues.original_size_x[1] && isDigitalOriginal)
          count++;
      }

      if (current.values.original_size_y && current.initialValues.original_size_y) {
        if (current.values.original_size_y[0] !== current.initialValues.original_size_y[0] && isDigitalOriginal)
          count++;
        if (current.values.original_size_y[1] !== current.initialValues.original_size_y[1] && isDigitalOriginal)
          count++;
      }

      if (current.values.token_status && current.initialValues.token_status) {
        if (current.values.token_status !== current.initialValues.token_status) count++;
      }

      if (current.values.curator_review && current.values.curator_review !== current.initialValues.curator_review)
        count++;

      artworksFiltersCount.set(count);
    }
  });

  return {
    subscribe,
    set,
    initData(response: ArtworksFiltersOptionsResponse, slug: string) {
      const options = getOptions(response);
      const valuesFromResponse = getValues(response);
      const paramsValues = getValueFromSlug(slug);

      const preparedArtists = paramsValues?.artists_slugs
        ? options.artists.filter((artist) => (paramsValues.artists_slugs as string).includes(artist.value.toString()))
        : [];
      const preparedCurators = paramsValues?.curators_slugs
        ? options.curators.filter((curator) =>
            (paramsValues.curators_slugs as string).includes(curator.value.toString()),
          )
        : [];
      const preparedStyles = paramsValues?.styles_slugs
        ? options.styles.filter((style) => (paramsValues.styles_slugs as string).includes(style.value.toString()))
        : [];
      const preparedMedium = paramsValues?.medium_slugs
        ? options.medium.filter((medium) => (paramsValues.medium_slugs as string).includes(medium.value.toString()))
        : [];
      const preparedCountries = paramsValues?.countries_slugs
        ? options.countries.filter((country) =>
            (paramsValues.countries_slugs as string).includes(country.value.toString()),
          )
        : [];

      const values = {
        ...valuesFromResponse,
        ...paramsValues,
        artists_slugs: preparedArtists,
        curators_slugs: preparedCurators,
        styles_slugs: preparedStyles,
        medium_slugs: preparedMedium,
        countries_slugs: preparedCountries,
      };

      update((current) => {
        return {
          ...current,
          initialized: true,
          initialValues: JSON.parse(
            JSON.stringify({
              ...defaultValues,
              price: valuesFromResponse.price,
              // curator_review: valuesFromResponse.curator_review,
              curator_review: paramsValues.curator_review || false,
              creation_year: valuesFromResponse.creation_year,
              original_size_x: valuesFromResponse.original_size_x,
              original_size_y: valuesFromResponse.original_size_y,
            }),
          ),
          updatedValues: JSON.parse(JSON.stringify(valuesFromResponse)),
          // updatedValues: JSON.parse(JSON.stringify(values)), // in previous line, valuesFromResponse was used instead of values and we always have different values and updatedValues after initial fetch and thats occurs an additional request based on their diff
          values,
          options: JSON.parse(JSON.stringify(options)),
          items_total_count: response.items_total_count,
        };
      });
    },
    updateData(response: ArtworksFiltersOptionsResponse, values: FormValues) {
      update((current) => {
        const updatedOptions = getUpdatedOptions(response, current.currentOption);

        return {
          ...current,
          options: JSON.parse(JSON.stringify(updatedOptions)),
          updatedValues: JSON.parse(JSON.stringify(values)),
          items_total_count: response.items_total_count,
        };
      });
    },
    updateCurrentOption(option: ArtworksFiltersModel['currentOption']) {
      update((current) => {
        return {
          ...current,
          options: {
            ...current.options,
            ...option,
          },
          currentOption: JSON.parse(JSON.stringify(option)),
        };
      });
    },
    resetValues() {
      update((current) => {
        return {
          ...current,
          values: JSON.parse(JSON.stringify(current.initialValues)),
        };
      });
    },
  };
};

export const artworksFiltersModel = createModel(defaultsData);
